import React, { useState, useEffect } from 'react';
import { getApi, postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';

const GeneralSettings = ({ isLoading, setIsLoading, setError }) => {
  const [companyDetail, setCompanyDetail] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isInitializing, setIsInitializing] = useState(true);
  const [isTouched, setIsTouched] = useState({
    name: false,
    email: false,
    phone: false
  });

  const fetchGeneralData = async () => {
    try {
      setIsLoading(true);
      setIsInitializing(true);
      setError(null);
      
      const resp = await getApi('/company-setting?per_page=50');
      
      if (resp?.success === true) {
        setCompanyDetail({
          name: resp?.data?.companySetting?.name || '',
          email: resp?.data?.companySetting?.email || '',
          phone: resp?.data?.companySetting?.phone || ''
        });
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch company settings');
      toast.error(error.message || 'Failed to fetch company settings');
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value.trim()) error = "Company name is required";
        break;
      case 'email':
        if (!value.trim()) error = "Email is required";
        else if (!/^\S+@\S+\.\S+$/.test(value)) error = "Invalid email format";
        break;
      case 'phone':
        if (!value.trim()) error = "Phone number is required";
        else if (!/^\d{10}$/.test(value)) error = "Phone number must be 10 digits";
        break;
      default:
        break;
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setCompanyDetail((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (isTouched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    setIsTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(companyDetail).forEach(key => {
      const error = validateField(key, companyDetail[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsTouched({
      name: true,
      email: true,
      phone: true
    });

    if (validateForm()) {
      try {
        setIsLoading(true);
        setError(null);
        const resp = await postApi('/company-setting', companyDetail);
        if (resp?.success === true) {
          toast.success(resp.msg || 'Settings saved successfully');
          await fetchGeneralData();
        } else {
          toast.error(resp.msg || 'Failed to save settings');
        }
      } catch (error) {
        setError(error.message || 'Failed to save company settings');
        toast.error(error.message || 'Failed to save company settings');
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.error('Please fix the errors in the form', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  useEffect(() => {
    fetchGeneralData();
  }, []);

  return (
    <div className='setting-inner'>
      {isInitializing ? (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className='row'>
          <div className='col-sm-6'>
            <div className='form-group'>
              <label>Company Name<strong>*</strong></label>
              <input
                type="text"
                name='name'
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                placeholder="Enter company Name"
                value={companyDetail?.name}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoading}
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
          </div>
          <div className='col-sm-6'>
            <div className='form-group'>
              <label>Default Email<strong>*</strong></label>
              <input
                type="email"
                name='email'
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                placeholder="Company@gmail.com"
                value={companyDetail?.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoading}
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>
          <div className='col-sm-6'>
            <div className='form-group'>
              <label>Company Phone<strong>*</strong></label>
              <input
                type="tel"
                name='phone'
                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                placeholder="1234567891"
                value={companyDetail?.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={isLoading}
              />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
          </div>
          <div className='col-sm-12'>
            <button
              className='save float-inline-end'
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;